import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, Container, CssBaseline } from '@mui/material';

import Logo from '../../../components/UI/Logo/Logo';
import useTranslations from '../../../hooks/useTranslations';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import { AuthActions } from '../../../store/entities/auth/auth.actions';
import { formatIdNumberDigit } from '../../../utils/formValidation/ValidationHelperFunctions';
import phoneExtensions from '../../../assets/data/phoneExtensions.json';
import { changePasswordValidation } from '../../../utils/formValidation/changePassword';
import Title from '../../../components/UI/Title/Title';
import Button from '../../../components/UI/Button/Button';

import './ChangePassword.scss';
import { getErrorMessages } from '../../../utils/formatters';
import { loadScriptByURL } from '../../Configuration/DragDrop/Utils';

const theme = createTheme();

const ChangePassword = () => {
  const [show, setShow] = useState<boolean>(false);
  const [showSuccessContent, setShowSuccessContent] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const [searchParams, setSearchParams] = useSearchParams();

  const { rcTranslate } = useTranslations();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=6Ld_ZD8hAAAAAGlNN69ssER3afJ-kSDDughsEOh_`,
      function () {}
    );
  }, []);

  const fields: IFormFields = {
    initialValues: {
      identificationNumber: '',
      phoneNumber: '',
      phoneExt: '',
      code: searchParams.get('code') ?? '',
      newPassword: '',
      passwordInstructions: '',
      confirmPassword: '',
    },
    formData: [
      {
        type: FieldTypes.TEXT,
        id: 'identificationNumber',
        label: rcTranslate('changePassword.fieldNames.idNum'),
        props: {
          inputProps: { maxLength: 9 },
        },
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'phoneWrapper',
        fields: [
          {
            type: FieldTypes.TEL,
            id: 'phoneNumber',
            label: rcTranslate('firstLogin.fieldNames.phoneNumberLabel'),
            props: { inputProps: { maxLength: 7 } },
            className: 'customerDetails__phoneNumber',
          },
          {
            type: FieldTypes.SELECT,
            id: 'phoneExt',
            label: '',
            className: 'customerDetails__phoneExt',
            items: phoneExtensions,
          },
        ],
        className: 'customerDetails__phoneWrapper',
      },

      {
        type: FieldTypes.PASSWORD,
        id: 'newPassword',
        label: rcTranslate('firstLogin.fieldNames.password'),
        props: {
          inputProps: { maxLength: 16 },
          offAoutocomplete: true,
        },
      },

      {
        type: FieldTypes.PASSWORD,
        id: 'confirmPassword',
        label: rcTranslate('firstLogin.fieldNames.confirmPassword'),
        props: {
          inputProps: { maxLength: 16 },
        },
      },
      {
        type: FieldTypes.PLAIN_TEXT,
        id: 'passwordInstructions',
        label: 'firstLogin.passwordInstructions',
        className: 'firstLogin_passwordInstructions',
      },
    ],
  };

  const changePassword = async (
    values: any,
    setSubmitting: (value: boolean) => void
  ) => {
    setSubmitting(false);
    (window as any).grecaptcha.ready(function () {
      (window as any).grecaptcha
        .execute('6Ld_ZD8hAAAAAGlNN69ssER3afJ-kSDDughsEOh_', {
          action: 'submit',
        })
        .then(async function (token: any) {
          let res: any = await dispatch(
            AuthActions.changePassword({
              identificationNumber: formatIdNumberDigit(
                values?.identificationNumber
              ),
              phoneNumber: values?.phoneExt + values?.phoneNumber,
              code: values?.code,
              newPassword: values?.newPassword,
              RecaptchaKey: token,
            })
          );
          if (res?.isSucceeded) {
            setShow(false);
            setShowSuccessContent(true);
          } else {
            let result: any = await getErrorMessages(res);
            let errorMessage: string = '';

            if (
              (process.env.REACT_APP_ENV === 'dev' &&
                res?.response?.status === 400) ||
              res?.response?.status !== 400
            ) {
              result?.forEach((element: any) => {
                errorMessage += ' ' + element.message;
              });
            } else {
              errorMessage = rcTranslate('errors.generalError');
            }
            setMessage(errorMessage);
            setShow(true);
          }

          // Add your logic to submit to your backend server here.
        });
    });
  };

  const navigateToLogin = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  let changePasswordContent = (
    <Box sx={{ mt: 8, width: '300px' }}>
      <Form
        fields={fields}
        formTitle={['changePassword.title']}
        formValidation={changePasswordValidation}
        onSubmit={changePassword}
        submitButtonTitle="button.confirm"
        submitButtonClassName="login__submitButton"
        formClassName="login__form"
        isDisabled={!searchParams.get('code')}
      />
    </Box>
  );

  let changePasswordSuccessContent = (
    <Box sx={{ mt: 8, width: '300px' }}>
      <Title text="changePassword.title" className="changePassword__title" />
      <div className="changePassword__subtitle">
        {rcTranslate('changePassword.success')}
      </div>
      <Button
        title="changePassword.login"
        className="changePassword__login"
        onClick={() => navigateToLogin()}
      />
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          className={'loginWrapper'}
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box>
            <Logo style={{ width: '250px' }} />
          </Box>

          {showSuccessContent
            ? changePasswordSuccessContent
            : changePasswordContent}
          {show && <p className="changePassword__errorMessage">{message}</p>}
        </Box>
      </Container>
    </ThemeProvider>
  );
};
export default ChangePassword;
